import { Component, OnInit, inject } from '@angular/core';
import { MatDialogRef } from '@angular/material/dialog';
import { I18nService } from '@shared/i18n';
import { IntlUtil } from '@shared/utils/intl.util';
import { FlagAssets } from 'src/assets/image/flag';
import { environment } from 'src/environments/environment';
import { Locales } from 'src/i18n';

type Option = {
  code: string;
  endonym: string;
  exonym: string;
  flag: string;
};

/**
 * The language switcher modal dialog.
 *
 * This switcher “bakes in” its options at the start, so as to not flicker its rendering as it's closing.
 */
@Component({
  selector: 'app-language-switcher-dialog',
  standalone: false,
  templateUrl: './language-switcher-dialog.component.html',
  styleUrl: './language-switcher-dialog.component.scss',
})
export class LanguageSwitcherDialogComponent implements OnInit {
  private readonly i18n = inject(I18nService);
  private readonly dialogRef = inject<MatDialogRef<LanguageSwitcherDialogComponent>>(MatDialogRef);

  /** The app's current language. */
  public language: string;
  /** Language switcher options. */
  public options: Option[] = [];

  ngOnInit(): void {
    const language = this.i18n.getCurrentLanguage();
    this.language = language;
    this.options = this.buildOptions(language);
  }

  buildOptions(language: string) {
    const options: Option[] = [];

    if (!environment.production) {
      // Debug mode: cimode exposes raw translation keys instead of translations.
      options.push({
        code: 'cimode',
        endonym: this.i18n.getPhrase('language.cimode'),
        exonym: 'cimode',
        flag: 'aq',
      });
    }

    for (const locale of Locales.list) {
      options.push({
        endonym: locale.endonym,
        exonym: IntlUtil.getLanguageName(locale.code, language),
        code: locale.code,
        flag: locale.flag,
      });
    }

    return options;
  }

  pickLanguage(code: string) {
    if (code === 'cimode') {
      this.i18n.changeLanguage(code);
    } else {
      const locale = Locales.getLocale(code);
      this.i18n.changeLanguage(locale.translation);
    }
    this.dialogRef.close();
  }

  getFlag(flag: string) {
    return FlagAssets.get(flag) ?? FlagAssets.get('404');
  }
}
